import * as React from 'react';

import SetNumber from '@/components/elements/Text/SetNumber';
import {
  BetStatus,
  BetType,
  SingleRaceRefund,
} from '@/lib/apollo/types/gen/graphql';
import { getBetTypeName, getHitSetNum } from '@/lib/race';
import { Odds } from '@/modelTypes';

import {
  FlexBox,
  RefundAmount,
  SetNumberText,
  Table,
  TableData,
  TableHeader,
  TableRow,
  TextBold,
} from './style';

interface RefundCardProps {
  singleRaceRefunds: SingleRaceRefund[];
  odds: Odds | null;
  isCanceled?: boolean;
}

const RefundCard: React.FC<RefundCardProps> = ({
  singleRaceRefunds,
  odds,
  isCanceled,
}) => {
  // 賭式の順番が固定のため配列で管理
  const orderBetTypes = [
    BetType.BracketQuinella,
    BetType.BracketPerfecta,
    BetType.Quinella,
    BetType.Perfecta,
    BetType.Trio,
    BetType.Trifecta,
    BetType.QuinellaPlace,
  ];

  const getSetNum = (srr: SingleRaceRefund): number => {
    return getHitSetNum(srr);
  };

  const getOddsRank = (betType: BetType, setNum: number): number | string => {
    if (!odds) {
      return '-';
    }
    const oddsDetail = odds[betType].detail.find(d => d.setNum === setNum);
    return oddsDetail ? oddsDetail.rank || 9999 : 9999;
  };

  const singleRaceRefundByBetType = (betType: BetType) => {
    return singleRaceRefunds.filter(srr => srr.betType === betType);
  };

  return (
    <div>
      <Table>
        <tbody>
          {orderBetTypes.map((betType, i) => (
            <React.Fragment key={`${betType}-${i}`}>
              {singleRaceRefundByBetType(betType).map((srr, j) => (
                <React.Fragment key={`${srr.betType}-${i}-${j}`}>
                  {Object.values(BetType).includes(srr.betType) && (
                    <TableRow betType={srr.betType}>
                      {j === 0 && (
                        <TableHeader
                          data-testid="betType"
                          rowSpan={singleRaceRefundByBetType(betType).length}
                        >
                          {getBetTypeName(srr.betType)}
                        </TableHeader>
                      )}
                      <TableData
                        betType={srr.betType}
                        betStatus={srr.betStatus}
                      >
                        {!isCanceled &&
                          (() => {
                            if (srr.betStatus === BetStatus.AllReturn) {
                              return <TextBold>全返還</TextBold>;
                            }
                            if (srr.betStatus === BetStatus.DeadHeatOver) {
                              return <TextBold>同着オーバー</TextBold>;
                            }
                            return (
                              <>
                                <FlexBox>
                                  <SetNumberText>
                                    <SetNumber
                                      setNums={[getSetNum(srr)]}
                                      betType={srr.betType}
                                    />
                                  </SetNumberText>
                                  <span data-testid="rank">
                                    {`(${getOddsRank(
                                      srr.betType,
                                      getSetNum(srr),
                                    )})`}
                                  </span>
                                </FlexBox>
                                {srr.betStatus === BetStatus.SpecialPayment ? (
                                  <TextBold>特払い</TextBold>
                                ) : (
                                  <RefundAmount data-testid="refundAmount">
                                    {srr.refund.toLocaleString()}円
                                  </RefundAmount>
                                )}
                              </>
                            );
                          })()}
                      </TableData>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default RefundCard;
